import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Toaster } from "sonner";
import { ClerkProvider } from "@clerk/react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import BottomNav from "@/components/app/BottomNav";
import InstallPrompt from "@/components/app/InstallPrompt";
import { AIAgentWidget } from "./components/AIAgentWidget";
import Auth from "@/pages/Auth";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import Feed from "@/pages/Feed";
import CreateActivity from "@/pages/CreateActivity";
import ActivityDetail from "@/pages/ActivityDetail";
import Profile from "@/pages/Profile";
import ProfileEdit from "@/pages/ProfileEdit";
import ProfileShop from "@/pages/ProfileShop";
import Settings from "@/pages/Settings";
import SettingsAccount from "@/pages/SettingsAccount";
import SettingsBlocked from "@/pages/SettingsBlocked";
import SettingsMutedWords from "@/pages/SettingsMutedWords";
import SettingsHelp from "@/pages/SettingsHelp";
import SafetyHub from "@/pages/SafetyHub";
import SettingsPrivacy from "@/pages/SettingsPrivacy";
import SettingsAppearance from "@/pages/SettingsAppearance";
import SettingsNotifications from "@/pages/SettingsNotifications";
import SettingsPeoplePrefs from "@/pages/SettingsPeoplePrefs";
import Swipe from "@/pages/Swipe";
import Matches from "@/pages/Matches";
import Guidelines from "@/pages/Guidelines";
import Legal from "@/pages/Legal";
import ImportantInfo from "@/pages/ImportantInfo";
import Chats from "@/pages/Chats";
import ChatDetail from "@/pages/ChatDetail";
import Notifications from "@/pages/Notifications";
import CalendarPage from "@/pages/Calendar";
import Spontaneous from "@/pages/Spontaneous";
import Posts from "@/pages/Posts";
import CreatePost from "@/pages/CreatePost";
import Coins from "@/pages/Coins";
import Vip from "@/pages/Vip";
import Friends from "@/pages/Friends";
import SearchPage from "@/pages/SearchPage";
import MyActivities from "@/pages/MyActivities";
import AdminVerifications from "@/pages/AdminVerifications";
import AdminTickets from "@/pages/AdminTickets";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminAccounts from "@/pages/AdminAccounts";
import MyPosts from "@/pages/MyPosts";
import ForgotPassword from "@/pages/ForgotPassword";
import LiveMap from "@/pages/LiveMap";
import useLocationSharing from "@/hooks/useLocationSharing";
import LoadingScreen from "@/components/app/LoadingScreen";
import "@/App.css";

function Shell({ children, hideNav = false }) {
  return (
    <div className="w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-[1960px] lg:px-12 mx-auto min-h-screen relative bg-[#FFFDF7] text-[#121212] shadow-2xl border-x border-[#0A0B0D]/5 overflow-x-hidden flex flex-col pb-28 sm:pb-32">
      <div className="flex-1 w-full">
        {children}
      </div>
      {!hideNav && <BottomNav />}
    </div>
  );
}

function ProtectedRoute({ children, hideNav = false }) {
  const { user, loading } = useAuth();
  useLocationSharing();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/auth" replace />;
  return <Shell hideNav={hideNav}>{children}</Shell>;
}

function PublicLanding() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to="/feed" replace />;
  return <Landing />;
}

function AuthPage() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) return <Navigate to="/feed" replace />;
  return <Auth />;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: detect session_id during render (NOT in useEffect) — preempts ProtectedRoute redirects.
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<PublicLanding />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/feed" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
      <Route path="/spontaneous" element={<ProtectedRoute><Spontaneous /></ProtectedRoute>} />
      <Route path="/create" element={<ProtectedRoute><CreateActivity /></ProtectedRoute>} />
      <Route path="/activity/:id" element={<ProtectedRoute><ActivityDetail /></ProtectedRoute>} />
      <Route path="/activity/:id/map" element={<ProtectedRoute hideNav><LiveMap /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/profile/edit" element={<ProtectedRoute><ProfileEdit /></ProtectedRoute>} />
      <Route path="/me/posts" element={<ProtectedRoute><MyPosts /></ProtectedRoute>} />
      <Route path="/u/:userId/posts" element={<ProtectedRoute><MyPosts /></ProtectedRoute>} />
      <Route path="/profile/shop" element={<ProtectedRoute><ProfileShop /></ProtectedRoute>} />
      <Route path="/u/:id" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/chats" element={<ProtectedRoute><Chats /></ProtectedRoute>} />
      <Route path="/chats/:id" element={<ProtectedRoute hideNav><ChatDetail /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/posts" element={<ProtectedRoute><Posts /></ProtectedRoute>} />
      <Route path="/post/new" element={<ProtectedRoute><CreatePost /></ProtectedRoute>} />
      <Route path="/coins" element={<ProtectedRoute><Coins /></ProtectedRoute>} />
      <Route path="/vip" element={<ProtectedRoute><Vip /></ProtectedRoute>} />
      <Route path="/friends" element={<ProtectedRoute><Friends /></ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
      <Route path="/mine" element={<ProtectedRoute><MyActivities /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/accounts" element={<ProtectedRoute><AdminAccounts /></ProtectedRoute>} />
      <Route path="/admin/verifications" element={<ProtectedRoute><AdminVerifications /></ProtectedRoute>} />
      <Route path="/admin/tickets" element={<ProtectedRoute><AdminTickets /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/settings/account" element={<ProtectedRoute><SettingsAccount /></ProtectedRoute>} />
      <Route path="/settings/privacy" element={<ProtectedRoute><SettingsPrivacy /></ProtectedRoute>} />
      <Route path="/settings/privacy/blocked" element={<ProtectedRoute><SettingsBlocked /></ProtectedRoute>} />
      <Route path="/settings/privacy/muted" element={<ProtectedRoute><SettingsMutedWords /></ProtectedRoute>} />
      <Route path="/settings/help" element={<ProtectedRoute><SettingsHelp /></ProtectedRoute>} />
      <Route path="/settings/help/safety-hub" element={<ProtectedRoute><SafetyHub /></ProtectedRoute>} />
      <Route path="/settings/appearance" element={<ProtectedRoute><SettingsAppearance /></ProtectedRoute>} />
      <Route path="/settings/notifications" element={<ProtectedRoute><SettingsNotifications /></ProtectedRoute>} />
      <Route path="/settings/people-preferences" element={<ProtectedRoute><SettingsPeoplePrefs /></ProtectedRoute>} />
      <Route path="/swipe" element={<ProtectedRoute><Swipe /></ProtectedRoute>} />
      <Route path="/matches" element={<ProtectedRoute><Matches /></ProtectedRoute>} />
      <Route path="/guidelines" element={<Shell hideNav><Guidelines /></Shell>} />
      <Route path="/legal" element={<Shell hideNav><Legal /></Shell>} />
      <Route path="/important-info" element={<ProtectedRoute><ImportantInfo /></ProtectedRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ForgotPassword />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ClerkProvider publishableKey={process.env.REACT_APP_CLERK_PUBLISHABLE_KEY}>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" toastOptions={{ style: { border: "1px solid rgba(15,17,21,0.08)", borderRadius: "16px", background: "rgba(255,255,255,0.95)", backdropFilter: "blur(16px)", color: "#0F1115", fontFamily: "DM Sans, sans-serif", fontWeight: 600, boxShadow: "0 8px 32px rgba(15,17,21,0.12)" } }} />
          <InstallPrompt />
          <AIAgentWidget />
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </ClerkProvider>
  );
}
